/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import * as React from "react";
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { customDialogSelector } from "../../../store/helpers/helperSelectors";
import { useStoreDispatch } from "../../../store/hooks";
import { setOpenCustomDailog } from "../../../store/helpers/helperReducers";
import Draggable from 'react-draggable';
import { PaperProps, Paper } from "@mui/material";
import { iconButtonSxProps } from "../../helpers";


function PaperComponent(props: PaperProps) {
  return (
    <Draggable handle="#customized-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
      <Paper {...props} />
    </Draggable>
  );
}

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
  "& .MuiPaper-root.MuiDialog-paper": {
    backgroundColor: "var(--card)",
    color: "var(--foreground)",
  },
}));

export interface DialogTitleProps {
  id: string;
  children?: React.ReactNode;
  onClose: () => void;
}

function BootstrapDialogTitle(props: DialogTitleProps) {
  const { children, onClose, ...other } = props;

  return (
    <DialogTitle sx={{ m: 0, p: 3, pl: 8, cursor: "move" }} {...other}>
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "var(--muted-foreground)",
            ...iconButtonSxProps,
          }}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
}

interface ICustomizedDilog {
  headerTitle: string;
  children?: React.ReactNode;
  dialogActions?: React.ReactNode;
  size?: "xs" | "sm" | "md" | "lg" | "xl";
}

const CustomizedDialogs: React.FC<ICustomizedDilog> = ({ children, headerTitle, dialogActions, size = "sm" }) => {
  const dispatch = useStoreDispatch();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down(size));
  const openCustomDialog = useSelector(customDialogSelector);
  const handleClose = () => {
    dispatch(setOpenCustomDailog(false));
  };

  const handleDlgClose = (event: any, reason: any) => {
    if (reason && reason === "backdropClick") {
      return;
    }
    dispatch(setOpenCustomDailog(false));
  }

  return (
    <div>
      <BootstrapDialog
        onClose={handleDlgClose}
        aria-labelledby="customized-dialog-title"
        open={openCustomDialog}
        fullScreen={fullScreen}
        fullWidth
        maxWidth={size}
        PaperComponent={PaperComponent}
      >
        <BootstrapDialogTitle id="customized-dialog-title" onClose={handleClose}>
          {headerTitle}
        </BootstrapDialogTitle>
        <DialogContent dividers>{children}</DialogContent>
        {dialogActions ? (
          <DialogActions>{dialogActions}</DialogActions>
        ) : (
          <DialogActions>
            <Button autoFocus onClick={handleClose}>
              Cancel
            </Button>
            <Button variant="contained" color="primary" autoFocus onClick={handleClose}>
              Save
            </Button>
          </DialogActions>
        )}
      </BootstrapDialog>
    </div>
  );
};

export default CustomizedDialogs;
